import { TERMINAL_STATES, POLL_INTERVAL_MS } from '../lib/scanStatus';

interface ScanStatusLike {
  status: string;
  startDate?: string;
  stopDate?: string;
}

interface ScanStatusCardProps {
  /** Latest polled status, or null while connecting. */
  status: ScanStatusLike | null;
  /** Poll/transport error, if any. */
  error: string | null;
  /** data-testid on the card (`scan-progress` / `discovery-scan-progress`). */
  testId: string;
  /** Result line shown once terminal — built by the caller from its own status shape. */
  resultText: string;
}

/**
 * Presentational Status / Started / Completed / Result card shared by ScanProgress
 * and DiscoveryScanProgress. Polling + result-text building live in the callers
 * (via usePolledStatus); this only renders the connecting / error / rows markup.
 */
export function ScanStatusCard({ status, error, testId, resultText }: ScanStatusCardProps) {
  if (error) {
    return (
      <div className="card" data-testid={testId}>
        <p className="badge badge-error">Error: {error}</p>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="card" data-testid={testId}>
        <p className="text-muted">Connecting to scan...</p>
      </div>
    );
  }

  const isTerminal = TERMINAL_STATES.has(status.status);
  const statusBadge =
    status.status === 'SUCCEEDED'
      ? 'badge-success'
      : status.status === 'RUNNING'
        ? 'badge-info'
        : status.status === 'QUEUED'
          ? 'badge-neutral'
          : 'badge-error';

  return (
    <div className="card" data-testid={testId}>
      <h3 className="text-lg font-semibold mb-md">Scan Progress</h3>
      <div className="flex flex-col gap-sm">
        <div className="flex items-center gap-md">
          <span className="text-base text-muted">Status:</span>
          <span className={`badge ${statusBadge}`}>{status.status}</span>
        </div>
        {status.startDate && (
          <div className="flex items-center gap-md">
            <span className="text-base text-muted">Started:</span>
            <span className="text-base">
              {new Date(status.startDate).toLocaleString()}
            </span>
          </div>
        )}
        {status.stopDate && (
          <div className="flex items-center gap-md">
            <span className="text-base text-muted">Completed:</span>
            <span className="text-base">
              {new Date(status.stopDate).toLocaleString()}
            </span>
          </div>
        )}
        {isTerminal && (
          <div className="flex items-center gap-md mt-md">
            <span className="text-base text-muted">Result:</span>
            <span className="text-base">{resultText}</span>
          </div>
        )}
        {!isTerminal && (
          <p className="text-xs text-muted mt-sm">
            Polling every {(POLL_INTERVAL_MS / 1000).toString()}s...
          </p>
        )}
      </div>
    </div>
  );
}
